// The zones the website knows (compiled from zones/**/zone.json into specs.ts) and the extension points zone owners
// use: their own section kinds and their 3D hero scenes.

import type { Catalogue } from "../catalogue.ts";
import { match } from "../router.ts";
import { parseZone, type Zone } from "./spec.ts";
import { ZONE_SPECS } from "./specs.ts";

const zones: Zone[] = [];
for (const raw of ZONE_SPECS) {
  const { zone, errors } = parseZone(raw);
  if (errors.length) console.warn(`zone ${(raw as { id?: string })?.id ?? "?"}: ${errors.join("; ")}`);
  if (zone && !zones.some((z) => z.id === zone.id)) zones.push(zone);
}

export function allZones(): readonly Zone[] {
  return zones;
}

/** A zone by id or slug. */
export function zoneById(idOrSlug: string): Zone | undefined {
  return zones.find((z) => z.id === idOrSlug) ?? zones.find((z) => z.slug === idOrSlug);
}

/**
 * Where a zone lives on the website: its own `/<slug>` when a page is routed there (an older page such as /events or
 * /store, or the zone itself once main.ts opts it in), else its preview at `/z/<id>`. Links never 404.
 */
export function zoneHref(z: Pick<Zone, "id" | "slug">): string {
  return match(`/${z.slug}`) ? `/${z.slug}` : `/z/${z.id}`;
}

/** What a section renderer gets. `catalogue` resolves once; sync sections don't need to wait for it. */
export interface SectionContext {
  zone: Zone;
  arg: string | null;
  catalogue: () => Promise<Catalogue>;
  now: number;
}

/**
 * A section renderer: an element, null when it has nothing to show (the section stays hidden: empty modules never
 * show), or a promise of either.
 */
export type SectionRender = (ctx: SectionContext) => HTMLElement | null | Promise<HTMLElement | null>;

const sections = new Map<string, SectionRender>();
const zoneSections = new Map<string, SectionRender>();
const builtIns = new Map<string, SectionRender>();

/**
 * Add a section kind (`"quest"` handles `"quest"` and `"quest:<arg>"`) for every zone. Zone-specific kinds should be
 * registered from the zone's own module. To change a kind for one zone only, use `registerZoneSection`.
 */
export function registerSection(kind: string, render: SectionRender): void {
  sections.set(kind, render);
}

/**
 * A section kind for one zone only, looked up before the global kinds and the built-ins, so the other zones keep
 * theirs. The way to wrap a built-in for yourself:
 *   registerZoneSection("events", "countdown", (ctx) => wrap(builtInSection("countdown")!(ctx)))
 */
export function registerZoneSection(zoneId: string, kind: string, render: SectionRender): void {
  zoneSections.set(`${zoneId}\u0000${kind}`, render);
}

/** The shell's own renderer for a built-in kind (rows, row, games, countdown, promos, text), whatever is registered over it. */
export function builtInSection(kind: string): SectionRender | undefined {
  return builtIns.get(kind);
}

/** page.ts registers the built-in kinds here when it loads. Not for zones: use registerSection or registerZoneSection. */
export function registerBuiltInSection(kind: string, render: SectionRender): void {
  builtIns.set(kind, render);
}

/**
 * The renderer for `kind`: the zone's own (with `zoneId`), else the global one, else the built-in. Zones that chain
 * `before = sectionRenderer(kind)` therefore hand other zones the built-in, not nothing.
 */
export function sectionRenderer(kind: string, zoneId?: string): SectionRender | undefined {
  return (zoneId === undefined ? undefined : zoneSections.get(`${zoneId}\u0000${kind}`)) ?? sections.get(kind) ?? builtIns.get(kind);
}

/** A mounted 3D hero; `dispose` runs when the page goes away. */
export interface HeroScene {
  dispose(): void;
}

/**
 * Mount a 3D hero into `host` (already sized, the poster underneath). Resolve once the first frame is drawn: the
 * shell fades the canvas in over the poster then. `reducedMotion`: draw one still frame and don't animate.
 */
export type HeroMount = (host: HTMLElement, opts: { zone: Zone; reducedMotion: boolean }) => Promise<HeroScene>;

const scenes = new Map<string, () => Promise<HeroMount>>();

/** Register a 3D hero by scene id. `load` should import the scene lazily, so three.js never blocks first paint. */
export function registerHeroScene(id: string, load: () => Promise<HeroMount>): void {
  scenes.set(id, load);
}

export function heroScene(id: string | undefined): (() => Promise<HeroMount>) | undefined {
  return id ? scenes.get(id) : undefined;
}

/**
 * A zone's own Home portal banner (richer than the default promo block). It must be cheap: no 3D, no network before
 * it paints, reduced motion honoured. Return null to fall back to the default banner.
 */
export type PortalRender = (zone: Zone, now: number) => HTMLElement | null;

const portals = new Map<string, PortalRender>();

export function registerPortal(zoneId: string, render: PortalRender): void {
  portals.set(zoneId, render);
}

export function portalRenderer(zoneId: string): PortalRender | undefined {
  return portals.get(zoneId);
}

/**
 * A different hero for a family of zones (wave 13 category worlds: the curator's pick with Play). Built synchronously
 * with the hero, so it must be cheap; `after` runs once the hero exists (fill in what needs the catalogue).
 */
export interface HeroDecor {
  /** a site path for the poster (e.g. a game's key art at /art/…), instead of the spec's */
  poster?: string;
  alt?: string;
  /** replaces the eyebrow, title and tagline block */
  text?: HTMLElement;
  after?: (hero: HTMLElement, opts: { posterOnly: boolean; reducedMotion: boolean }) => void;
}

export type HeroDecorRender = (zone: Zone) => HeroDecor | null;

const decors: HeroDecorRender[] = [];

/** Register a hero decorator; it returns null for zones it doesn't decorate. The first non-null wins. */
export function registerHeroDecor(render: HeroDecorRender): void {
  decors.push(render);
}

export function heroDecor(zone: Zone): HeroDecor | null {
  for (const d of decors) {
    const out = d(zone);
    if (out) return out;
  }
  return null;
}
